import React, { useMemo, useState } from 'react';
import FullscreenBtn from '../components/FullscreenBtn';
import { generateClient } from 'aws-amplify/api';
import * as mutations from '../graphql/mutations';
import useOperationalStore, { CLOSED_STATUSES, fmtDur, getStageLabel } from '../platform/store/useOperationalStore';
import {
  coerceNotesArray,
  createServiceSummaryJson,
  getCategorySummary,
  getCycleMs,
  getPhotoCount,
  getServiceRecordSummary,
  getTechSummary,
  noteToText,
  tsToMs,
} from '../platform/history/serviceRecord';
import './HistoryView.css';

const client = generateClient();

const DATE_FILTERS = [
  { id: '30', label: '30 days', days: 30 },
  { id: '90', label: '90 days', days: 90 },
  { id: '365', label: '1 year', days: 365 },
  { id: 'all', label: 'All time', days: null },
];

function formatDate(ts) {
  const ms = tsToMs(ts);
  if (!ms) return 'Not stamped';
  return new Date(ms).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function categoriesText(truck) {
  return getCategorySummary(truck).join(' ');
}

function matchesSearch(truck, search) {
  if (!search) return true;
  const notes = coerceNotesArray(truck.notes);
  const haystack = [
    truck.repairOrder,
    truck.companyName,
    truck.truckNumber,
    truck.vin,
    truck.make,
    truck.model,
    truck.year,
    truck.reason,
    truck.contactPerson,
    truck.phone,
    truck.email,
    ...(truck.techNumbers || []),
    categoriesText(truck),
    notes.map(noteToText).join(' '),
  ].filter(Boolean).join(' ').toLowerCase();

  return search.toLowerCase().split(/\s+/).every(term => haystack.includes(term));
}

function downloadJson(filename, value) {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function HistoryView() {
  const trucks = useOperationalStore(s => s.trucks);
  const loading = useOperationalStore(s => s.loading);
  const setSelectedTruckId = useOperationalStore(s => s.setSelectedTruckId);
  const localUpdateTruck = useOperationalStore(s => s.localUpdateTruck);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [dateFilter, setDateFilter] = useState('90');
  const [syncing, setSyncing] = useState(false);

  const closed = useMemo(
    () => trucks.filter(t => CLOSED_STATUSES.includes(t.status)),
    [trucks]
  );

  const visible = useMemo(() => {
    const selectedDate = DATE_FILTERS.find(f => f.id === dateFilter) || DATE_FILTERS[1];
    const cutoff = selectedDate.days ? Date.now() - selectedDate.days * 24 * 60 * 60 * 1000 : null;

    return closed
      .filter(truck => status === 'all' || truck.status === status)
      .filter(truck => {
        if (!cutoff) return true;
        const closedAt = tsToMs(truck.completionTime || truck.lastStatusChange);
        return closedAt ? closedAt >= cutoff : true;
      })
      .filter(truck => matchesSearch(truck, search.trim()))
      .sort((a, b) => (tsToMs(b.completionTime || b.lastStatusChange) || 0) - (tsToMs(a.completionTime || a.lastStatusChange) || 0));
  }, [closed, dateFilter, search, status]);

  const stats = useMemo(() => {
    const completed = closed.filter(t => t.status === 'completed').length;
    const voided = closed.filter(t => t.status === 'voided').length;
    const photoCount = closed.reduce((sum, truck) => sum + getPhotoCount(coerceNotesArray(truck.notes)), 0);
    return { completed, voided, photoCount };
  }, [closed]);

  function handleExportSummary() {
    const payload = {
      exportedAt: new Date().toISOString(),
      filters: { search, status, dateFilter },
      count: visible.length,
      records: visible.map(getServiceRecordSummary),
    };
    downloadJson(`rekon-history-${new Date().toISOString().slice(0, 10)}.json`, payload);
  }

  async function handleSyncSummaries() {
    const missing = visible.filter(truck => !truck.serviceSummary);
    if (!missing.length || syncing) return;
    setSyncing(true);
    try {
      for (const truck of missing) {
        const serviceSummary = createServiceSummaryJson(truck);
        localUpdateTruck(truck.id, { serviceSummary });
        await client.graphql({
          query: mutations.updateTruck,
          variables: { input: { id: truck.id, serviceSummary } },
        });
      }
    } catch (err) {
      console.error('Could not sync service summaries:', err);
      alert('Some summaries could not be synced. Make sure the backend schema has been pushed.');
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="history-view">
      <div className="vtb">
        <div className="vtb-left">
          <div className="vtb-stat"><span className="vtb-stat-val">{stats.completed}</span><span className="vtb-stat-lbl">Completed</span></div>
          <div className="vtb-divider" />
          <div className="vtb-stat"><span className="vtb-stat-val">{stats.voided}</span><span className="vtb-stat-lbl">Voided</span></div>
          <div className="vtb-divider" />
          <div className="vtb-stat"><span className="vtb-stat-val">{stats.photoCount}</span><span className="vtb-stat-lbl">Photos</span></div>
        </div>
        <div className="vtb-right"><FullscreenBtn /></div>
      </div>

      <section className="history-toolbar" aria-label="History filters">
        <input
          className="history-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search RO, company, unit, VIN, tech, category, note..."
        />
        <select value={status} onChange={e => setStatus(e.target.value)}>
          <option value="all">All closed</option>
          <option value="completed">Completed</option>
          <option value="voided">Voided</option>
        </select>
        <select value={dateFilter} onChange={e => setDateFilter(e.target.value)}>
          {DATE_FILTERS.map(filter => (
            <option key={filter.id} value={filter.id}>{filter.label}</option>
          ))}
        </select>
        <button type="button" className="history-export" onClick={handleExportSummary}>
          Export
        </button>
        <button type="button" className="history-export history-export--ghost" onClick={handleSyncSummaries} disabled={syncing}>
          {syncing ? 'Syncing' : 'Sync'}
        </button>
      </section>

      <div className="history-list-wrap">
        <table className="history-table">
          <thead>
            <tr>
              <th>Job</th>
              <th>Customer / Unit</th>
              <th>Closed</th>
              <th>Duration</th>
              <th>Techs</th>
              <th>Files</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="history-empty">Loading history...</td></tr>
            )}
            {!loading && visible.length === 0 && (
              <tr><td colSpan={7} className="history-empty">No closed jobs match this view.</td></tr>
            )}
            {visible.map(truck => {
              const notes = coerceNotesArray(truck.notes);
              const duration = getCycleMs(truck);
              const techs = getTechSummary(truck);

              return (
                <tr key={truck.id} className="history-row" onClick={() => setSelectedTruckId(truck.id)}>
                  <td>
                    <div className="history-job">{truck.repairOrder || 'No RO'}</div>
                    <div className="history-muted">{categoriesText(truck) || truck.reason || 'No category'}</div>
                  </td>
                  <td>
                    <div className="history-main">{truck.companyName || 'Unknown customer'}</div>
                    <div className="history-muted">
                      {[truck.truckNumber, truck.vin].filter(Boolean).join(' / ') || 'No unit details'}
                    </div>
                  </td>
                  <td>{formatDate(truck.completionTime || truck.lastStatusChange)}</td>
                  <td>{duration ? fmtDur(duration) : '-'}</td>
                  <td>
                    {techs.length ? (
                      <div className="history-techs">
                        {techs.map(tech => <span key={tech}>{tech}</span>)}
                      </div>
                    ) : (
                      <span className="history-muted">Unassigned</span>
                    )}
                  </td>
                  <td>
                    <div className="history-files">
                      <span>{notes.length} notes</span>
                      <span>{getPhotoCount(notes)} photos</span>
                    </div>
                  </td>
                  <td>
                    <span className={`history-status history-status--${truck.status}`}>
                      {getStageLabel(truck.status)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
